import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from "react";
import { getMediaUrl, getImagenPreviewUrl } from "../../../utils/api";
import "./VolcanLineaTiempo.css";
import "../filtros/SgcFiltrosSidebar.css";
import "../descripcion/VolcanDescripcion.css";
import { normalizeAdminRichText } from "../../../utils/richText";
import { formatDateLongEsCO } from "../../../utils/dates";
import { useNavigate } from "react-router-dom";
import SectionTitleIcon from "../menu/SectionTitleIcon";
import SgcFiltrosHeader from "../filtros/SgcFiltrosHeader";
import SgcFiltroSection from "../filtros/SgcFiltroSection";
import SgcFiltrosFooter from "../filtros/SgcFiltrosFooter";
import { useSgcFiltersPanelHeight } from "../../../hooks/useSgcFiltersPanelHeight";
import excelIcon from "../../../assets/images/ui/descarga_excel.png";
import bannerImg from "../../../assets/images/ui/banner.png";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

function countElementLines(el) {
  if (!el) return 1;
  const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight);
  if (!Number.isFinite(lineHeight) || lineHeight <= 0) return 1;
  return Math.max(1, Math.round(el.scrollHeight / lineHeight));
}

function heroDescriptionLineClamp(titleLines) {
  const clampedTitleLines = Math.min(2, Math.max(1, titleLines));
  return Math.max(3, 6 - clampedTitleLines);
}

const EVENT_COLORS = {
  desastre: "#390302",
  actividad: "#DD6E1C",
  descripcion: "#FEB912",
  otros_eventos_geologicos: "#C8C8C8",
};

function eventLabelTextColor(tipo) {
  return tipo === "desastre" ? "#ffffff" : "#1a1a1a";
}

function timelineCardTypeTextColor(tipo) {
  return tipo === "desastre" || tipo === "actividad" ? "#ffffff" : "#1a1a1a";
}

function eventHasMoreInfo(event) {
  return Boolean(event?.tiene_generalidades || event?.generalidades);
}

const TIPO_FILTROS = [
  {
    key: "desastre",
    label: "Evento destacado",
    color: EVENT_COLORS.desastre,
    tooltip: "Evento asociado al volcán con afectación en la región.",
  },
  {
    key: "actividad",
    label: "Actividad",
    color: EVENT_COLORS.actividad,
    tooltip:
      "Reporte de actividad volcánica (humo, ceniza u otras manifestaciones).",
  },
  {
    key: "descripcion",
    label: "Descripción",
    color: EVENT_COLORS.descripcion,
    tooltip:
      "Información descriptiva del volcán sin actividad reportada para ese momento.",
  },
  {
    key: "otros_eventos_geologicos",
    label: "Otros eventos geológicos",
    color: EVENT_COLORS.otros_eventos_geologicos,
    tooltip:
      "Sismos o movimientos en masa importantes en la región cercana al volcán.",
  },
];

const TIPO_LABEL = {
  desastre: "Evento destacado",
  actividad: "Actividad",
  descripcion: "Descripción",
  otros_eventos_geologicos: "Otros eventos geológicos",
};

function htmlToPlainText(value) {
  if (value == null) return "";
  const s = String(value);
  if (!s.trim()) return "";
  if (!/[<>]/.test(s)) return s.trim();
  try {
    const doc = new DOMParser().parseFromString(s, "text/html");
    return String(doc?.body?.textContent ?? "")
      .replace(/\s+/g, " ")
      .trim();
  } catch {
    return s
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
}

async function buildAndDownloadExcel({
  filteredEvents,
  volcanId,
  volcanNombre,
}) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Servicio Geológico Colombiano";
  wb.created = new Date();
  const ws = wb.addWorksheet("Línea de tiempo", {
    properties: { defaultRowHeight: 18 },
    views: [{ state: "frozen", ySplit: 7 }],
  });

  ws.getColumn(1).width = 10;
  ws.getColumn(2).width = 22;
  ws.getColumn(3).width = 14;
  ws.getColumn(4).width = 64;

  ws.getRow(1).height = 26;
  ws.getRow(2).height = 26;
  ws.getRow(3).height = 26;

  const bannerBuf = await fetch(bannerImg).then((r) => r.arrayBuffer());
  const bannerId = wb.addImage({ buffer: bannerBuf, extension: "png" });
  ws.addImage(bannerId, {
    tl: { col: 0, row: 0 },
    br: { col: 4, row: 3 },
    editAs: "oneCell",
  });

  ws.mergeCells("A4:D4");
  const nombre = String(volcanNombre ?? "").trim();
  ws.getCell("A4").value = `Reporte línea de tiempo (Volcán: ${
    nombre || `ID ${volcanId}`
  })`;
  ws.getCell("A4").font = { bold: true, color: { argb: "FF1E4D2B" }, size: 11 };
  ws.getCell("A4").alignment = { vertical: "middle", horizontal: "center" };
  ws.getRow(4).height = 20;

  ws.mergeCells("A5:D5");
  ws.getCell("A5").value = `Generado: ${new Date().toLocaleString("es-CO")}`;
  ws.getCell("A5").font = { color: { argb: "FF646464" }, size: 10 };
  ws.getCell("A5").alignment = { vertical: "middle", horizontal: "center" };
  ws.getRow(5).height = 18;

  ws.mergeCells("A6:D6");
  ws.getRow(6).height = 6;

  const headerRow = ws.addRow(["ID", "FECHA", "TIPO", "DESCRIPCIÓN"]);
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  headerRow.alignment = { vertical: "middle", horizontal: "center" };
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF005833" },
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FFBFC9C2" } },
      left: { style: "thin", color: { argb: "FFBFC9C2" } },
      bottom: { style: "thin", color: { argb: "FFBFC9C2" } },
      right: { style: "thin", color: { argb: "FFBFC9C2" } },
    };
  });

  (filteredEvents || []).forEach((e) => {
    const tipo = TIPO_LABEL[e?.tipo] || e?.tipo || "—";
    const fecha = formatDateLongEsCO(e?.fecha_inicial);
    const desc = htmlToPlainText(e?.descripcion) || "—";
    const id = e?.id ?? "—";
    const row = ws.addRow([id, fecha, tipo, desc]);
    row.alignment = { vertical: "top", wrapText: true };
    row.getCell(1).alignment = { vertical: "top", horizontal: "center" };
    row.getCell(2).alignment = { vertical: "top", horizontal: "left" };
    row.getCell(3).alignment = { vertical: "top", horizontal: "center" };
    row.getCell(4).alignment = {
      vertical: "top",
      horizontal: "left",
      wrapText: true,
    };
    row.eachCell((cell) => {
      cell.border = {
        top: { style: "thin", color: { argb: "FFE0E4E5" } },
        left: { style: "thin", color: { argb: "FFE0E4E5" } },
        bottom: { style: "thin", color: { argb: "FFE0E4E5" } },
        right: { style: "thin", color: { argb: "FFE0E4E5" } },
      };
    });
  });

  ws.eachRow((row, rowNumber) => {
    if (rowNumber >= 8) row.height = Math.max(row.height || 18, 18);
  });

  const buf = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    "reporte_linea_tiempo.xlsx",
  );
}

function anioOCorto(e) {
  if (e.fecha_inicial) {
    const part = e.fecha_inicial.split("-")[0];
    if (part) return part;
  }
  return e.title || "—";
}

export default function VolcanLineaTiempo({
  timeline,
  volcanId,
  volcanNombre,
  initialTimelineEventId,
}) {
  const events = useMemo(
    () => (Array.isArray(timeline) ? timeline : []),
    [timeline],
  );

  const [filtersOpen, setFiltersOpen] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return !window.matchMedia("(max-width: 720px)").matches;
  });
  const [sortKey, setSortKey] = useState("anio_antiguo");
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(max-width: 720px)").matches;
  });

  const [filterTipo, setFilterTipo] = useState({
    desastre: true,
    actividad: true,
    descripcion: true,
    otros_eventos_geologicos: true,
  });

  const { minYear, maxYear } = useMemo(() => {
    const years = events
      .map((e) => Number(String(e?.fecha_inicial ?? "").split("-")[0]))
      .filter((y) => Number.isFinite(y) && y > 0);
    if (years.length === 0) return { minYear: 0, maxYear: 0 };
    return { minYear: Math.min(...years), maxYear: Math.max(...years) };
  }, [events]);

  const [yearFrom, setYearFrom] = useState(minYear || 0);
  const [yearTo, setYearTo] = useState(maxYear || 0);
  const [searchQuery, setSearchQuery] = useState("");
  const desktopContentRef = useRef(null);
  const filtersAsideRef = useRef(null);

  useEffect(() => {
    if (!minYear || !maxYear) return;
    setYearFrom((v) => (v ? Math.max(minYear, Math.min(v, maxYear)) : minYear));
    setYearTo((v) => (v ? Math.max(minYear, Math.min(v, maxYear)) : maxYear));
  }, [minYear, maxYear]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(max-width: 720px)");
    const sync = () => setIsMobile(Boolean(mql.matches));
    sync();

    if (mql.addEventListener) {
      mql.addEventListener("change", sync);
      return () => mql.removeEventListener("change", sync);
    }

    mql.addListener(sync);
    return () => mql.removeListener(sync);
  }, []);

  const filteredEvents = useMemo(() => {
    const q = String(searchQuery || "")
      .trim()
      .toLowerCase();
    const from = Number(yearFrom) || 0;
    const to = Number(yearTo) || 0;
    const lo = from && to ? Math.min(from, to) : from || to || 0;
    const hi = from && to ? Math.max(from, to) : from || to || 0;

    const filtered = events.filter((e) => {
      const k = e?.tipo;
      if (k && k in filterTipo && !filterTipo[k]) return false;

      if (lo || hi) {
        const y = Number(String(e?.fecha_inicial ?? "").split("-")[0]);
        if (Number.isFinite(y)) {
          if (lo && y < lo) return false;
          if (hi && y > hi) return false;
        }
      }

      if (q) {
        const title = String(e?.title ?? "").toLowerCase();
        const desc = htmlToPlainText(e?.descripcion).toLowerCase();
        if (!title.includes(q) && !desc.includes(q)) return false;
      }

      return true;
    });

    const withIndex = filtered.map((e, idx) => ({ ...e, __idx: idx }));

    const pickDate = (x) =>
      x?.fecha_inicial ?? x?.fecha ?? x?.created_at ?? null;
    const pickYear = (x) => {
      const part = String(pickDate(x) ?? "").split("-")[0];
      const y = Number(part);
      return Number.isFinite(y) ? y : null;
    };

    const hasAnyYear = withIndex.some((x) => pickYear(x) != null);
    if (!hasAnyYear) return withIndex;

    const oldestFirst = sortKey !== "anio_reciente";
    return [...withIndex].sort((a, b) => {
      const ya = pickYear(a);
      const yb = pickYear(b);
      if (ya == null && yb == null) return a.__idx - b.__idx;
      if (ya == null) return 1;
      if (yb == null) return -1;
      return oldestFirst ? ya - yb : yb - ya;
    });
  }, [events, filterTipo, yearFrom, yearTo, searchQuery, sortKey]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedRef = useRef(null);
  const heroTitleRef = useRef(null);
  const [heroDescLines, setHeroDescLines] = useState(5);

  useSgcFiltersPanelHeight(
    desktopContentRef,
    filtersAsideRef,
    !isMobile && filtersOpen,
    filteredEvents.length,
    selectedIndex,
    filtersOpen,
  );

  const appliedInitialTimelineFocus = useRef(false);
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  const openEventDetail = (event) => {
    if (!event) return;
    if (eventHasMoreInfo(event)) {
      navigate(`/evento-detalle?volcanId=${volcanId}&eventoId=${event.id}`);
      return;
    }
    setModalOpen(true);
  };
  const [excelBusy, setExcelBusy] = useState(false);
  const timelineTableRef = useRef(null);

  const selectedEvent = filteredEvents[selectedIndex];

  useLayoutEffect(() => {
    if (isMobile) {
      setHeroDescLines(5);
      return;
    }

    const measure = () => {
      const titleLines = countElementLines(heroTitleRef.current);
      setHeroDescLines(heroDescriptionLineClamp(titleLines));
    };

    measure();
    const raf = window.requestAnimationFrame(measure);
    return () => window.cancelAnimationFrame(raf);
  }, [selectedEvent?.id, selectedEvent?.title, isMobile]);

  const mobileExcerpt = (e) => {
    const text =
      htmlToPlainText(e?.descripcion) ||
      htmlToPlainText(e?.generalidades) ||
      "";
    const t = String(text).trim().replace(/\s+/g, " ");
    if (!t) return "";
    return t.length > 120 ? t.slice(0, 120) + "…" : t;
  };

  useEffect(() => {
    setSelectedIndex((i) => {
      if (filteredEvents.length === 0) return 0;
      return Math.min(i, filteredEvents.length - 1);
    });
  }, [filteredEvents.length]);

  useEffect(() => {
    appliedInitialTimelineFocus.current = false;
  }, [initialTimelineEventId, volcanId]);

  useEffect(() => {
    if (initialTimelineEventId == null || initialTimelineEventId === "") return;
    const idStr = String(initialTimelineEventId).trim();
    if (!/^\d+$/.test(idStr)) return;
    if (appliedInitialTimelineFocus.current) return;
    if (filteredEvents.length === 0) return;
    const idx = filteredEvents.findIndex((e) => String(e.id) === idStr);
    if (idx < 0) return;
    appliedInitialTimelineFocus.current = true;
    setSelectedIndex(idx);
  }, [filteredEvents, initialTimelineEventId]);

  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [selectedIndex]);

  const next = () => {
    if (filteredEvents.length === 0) return;
    setSelectedIndex((prev) =>
      prev === filteredEvents.length - 1 ? 0 : prev + 1,
    );
  };

  const prev = () => {
    if (filteredEvents.length === 0) return;
    setSelectedIndex((prev) =>
      prev === 0 ? filteredEvents.length - 1 : prev - 1,
    );
  };

  const toggleFiltro = (key) => {
    setFilterTipo((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const clearFilters = () => {
    setFilterTipo({
      desastre: true,
      actividad: true,
      descripcion: true,
      otros_eventos_geologicos: true,
    });
    setSearchQuery("");
    setSortKey("anio_antiguo");
    if (minYear && maxYear) {
      setYearFrom(minYear);
      setYearTo(maxYear);
    }
  };

  if (events.length === 0) {
    return (
      <p className="timeline-filter-empty timeline-filter-empty--solo">
        No hay datos para mostrar en Línea de tiempo
      </p>
    );
  }

  const renderTipoPill = ({
    key,
    label,
    color,
    tooltip,
    fullWidth = false,
  }) => (
    <label
      key={key}
      className={`timeline-tipo-pill${filterTipo[key] ? " is-checked" : ""}${
        fullWidth ? " timeline-tipo-pill--full" : ""
      }`}
      aria-label={`${label}: ${tooltip}`}
    >
      <span
        className="timeline-tipo-swatch"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      <span className="timeline-tipo-label">{label}</span>
      <input
        type="checkbox"
        checked={filterTipo[key]}
        onChange={() => toggleFiltro(key)}
        onClick={(e) => {
          // Evita que el foco se quede en el checkbox y mantenga el tooltip visible
          e.currentTarget.blur();
        }}
      />
      <span className="timeline-tipo-check" aria-hidden>
        <i className="bi bi-check-lg" />
      </span>
      <span className="timeline-tipo-tooltip" role="tooltip">
        {tooltip}
      </span>
    </label>
  );

  const tipoFiltroByKey = Object.fromEntries(
    TIPO_FILTROS.map((item) => [item.key, item]),
  );

  const renderFiltersBody = () => (
    <div className="sgc-filtros__body">
      <SgcFiltroSection
        icon="layers"
        label="Tipos de evento"
        className="sgc-filtro-block--tipos"
      >
        <div
          className="timeline-tipos-grid timeline-tipos-grid--sidebar"
          role="group"
          aria-label="Tipos de evento"
        >
          {renderTipoPill(tipoFiltroByKey.descripcion)}
          {renderTipoPill(tipoFiltroByKey.actividad)}
          {renderTipoPill({
            ...tipoFiltroByKey.desastre,
            fullWidth: true,
          })}
          {renderTipoPill({
            ...tipoFiltroByKey.otros_eventos_geologicos,
            fullWidth: true,
          })}
        </div>
      </SgcFiltroSection>

      <SgcFiltroSection icon="calendar3" label="Rango de años">
        <div className="timeline-years">
          <label className="timeline-year-field">
            <span>Desde</span>
            <input
              type="number"
              inputMode="numeric"
              value={yearFrom || ""}
              min={minYear || undefined}
              max={maxYear || undefined}
              placeholder={minYear ? String(minYear) : "0000"}
              onChange={(e) => setYearFrom(Number(e.target.value || 0))}
            />
          </label>
          <label className="timeline-year-field">
            <span>Hasta</span>
            <input
              type="number"
              inputMode="numeric"
              value={yearTo || ""}
              min={minYear || undefined}
              max={maxYear || undefined}
              placeholder={maxYear ? String(maxYear) : "0000"}
              onChange={(e) => setYearTo(Number(e.target.value || 0))}
            />
          </label>
        </div>

        {minYear && maxYear ? (
          <div className="timeline-range" aria-hidden="true">
            <div className="timeline-range-track" />
            <input
              type="range"
              min={minYear}
              max={maxYear}
              value={Math.min(
                Number(yearFrom) || minYear,
                Number(yearTo) || maxYear,
              )}
              onChange={(e) => setYearFrom(Number(e.target.value))}
            />
            <input
              type="range"
              min={minYear}
              max={maxYear}
              value={Math.max(
                Number(yearFrom) || minYear,
                Number(yearTo) || maxYear,
              )}
              onChange={(e) => setYearTo(Number(e.target.value))}
            />
          </div>
        ) : null}
      </SgcFiltroSection>

      <SgcFiltroSection icon="sort-down" label="Ordenar por">
        <select
          className="sgc-filtro-select"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
        >
          <option value="anio_antiguo">Año (más antiguo)</option>
          <option value="anio_reciente">Año (más reciente)</option>
        </select>
      </SgcFiltroSection>
    </div>
  );

  const renderFiltersFooter = () => (
    <div className="sgc-filtros__footer">
      <SgcFiltroSection icon="search" label="Búsqueda">
        <label className="sgc-filtro-search" htmlFor="timeline-search">
          <i className="bi bi-search" aria-hidden />
          <input
            id="timeline-search"
            type="search"
            placeholder="Ej. erupción, nevada, Ruiz…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
            spellCheck="false"
          />
        </label>
      </SgcFiltroSection>
      <SgcFiltrosFooter
        unwrapped
        summary={
          <>
            <strong>{filteredEvents.length}</strong> eventos encontrados
          </>
        }
        onClear={clearFilters}
      />
    </div>
  );

  const renderFiltersAside = () => (
    <aside
      ref={filtersAsideRef}
      className="sgc-filtros sgc-filtros--section-sidebar sgc-filtros--timeline-sidebar"
      aria-label="Filtros"
    >
      <SgcFiltrosHeader onCollapse={() => setFiltersOpen(false)} />
      {renderFiltersBody()}
      {renderFiltersFooter()}
    </aside>
  );

  const renderMobileEvents = () => (
    <div className="timeline-mobile">
      <div className="timeline-mobile-line" aria-hidden />
      <div className="timeline-mobile-list">
        {filteredEvents.map((e, i) => {
          const color = EVENT_COLORS[e.tipo] || "#999";
          const yearLine = anioOCorto(e);
          const isActive = selectedEvent?.id === e.id;
          const excerpt = mobileExcerpt(e);

          return (
            <div key={e.id} className="timeline-mobile-item">
              <div
                className="timeline-dot timeline-mobile-dot"
                style={{ backgroundColor: color }}
                aria-hidden
              />
              <div
                className={`timeline-card timeline-mobile-card${
                  isActive ? " timeline-mobile-card--active" : ""
                }`}
                style={{ borderTop: `4px solid ${color}` }}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedIndex(i)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") {
                    ev.preventDefault();
                    setSelectedIndex(i);
                  }
                }}
              >
                <div className="timeline-mobile-top">
                  <div className="timeline-year">{yearLine}</div>
                  <div
                    className="timeline-card-type"
                    style={{
                      backgroundColor: color,
                      color: timelineCardTypeTextColor(e.tipo),
                    }}
                  >
                    {TIPO_LABEL[e.tipo] ?? e.tipo ?? "Evento"}
                  </div>
                </div>
                <div className="timeline-title">{e.title}</div>
                {excerpt ? (
                  <div className="timeline-mobile-desc">{excerpt}</div>
                ) : null}

                <div className="timeline-mobile-actions">
                  <button
                    type="button"
                    className="timeline-action-btn png"
                    onClick={() => {
                      setSelectedIndex(i);
                      openEventDetail(e);
                    }}
                  >
                    <i className="bi bi-eye" />
                    <span> Ver detalle</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderHeroPreview = () => {
    if (!selectedEvent) {
      return <div className="timeline-hero-empty" aria-hidden="true" />;
    }

    return (
      <div className="preview preview--compact preview--hero">
        <button
          type="button"
          className="arrow left"
          onClick={prev}
          aria-label="Anterior"
        >
          ‹
        </button>

        <div className="preview-content preview-content--compact">
          <div
            className={`preview-image-frame preview-image-frame--compact${
              selectedEvent.imagen_evento ? " is-clickable" : ""
            }`}
            role={selectedEvent.imagen_evento ? "button" : undefined}
            tabIndex={selectedEvent.imagen_evento ? 0 : undefined}
            aria-label={
              selectedEvent.imagen_evento ? "Ver detalle (imagen)" : undefined
            }
            onClick={() => {
              if (!selectedEvent.imagen_evento) return;
              openEventDetail(selectedEvent);
            }}
            onKeyDown={(e) => {
              if (!selectedEvent.imagen_evento) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openEventDetail(selectedEvent);
              }
            }}
          >
            {selectedEvent.imagen_evento ? (
              <img
                src={getImagenPreviewUrl(selectedEvent.imagen_evento)}
                alt={selectedEvent.title}
                className="preview-image"
              />
            ) : (
              <div className="preview-image-placeholder">Sin imagen</div>
            )}
          </div>

          <div className="preview-info preview-info--compact">
            <h3
              ref={heroTitleRef}
              className="seccion-titulo preview-event-title"
            >
              {selectedEvent.title}
            </h3>

            {selectedEvent.descripcion ? (
              <p
                className="descripcion-texto timeline-preview-descripcion"
                style={{ "--tl-desc-lines": heroDescLines }}
              >
                {htmlToPlainText(selectedEvent.descripcion)}
              </p>
            ) : null}

            <div className="preview-actions preview-actions--fixed">
              <button
                type="button"
                className="timeline-action-btn png"
                onClick={() => openEventDetail(selectedEvent)}
              >
                <i className="bi bi-eye" />
                <span> Ver detalle</span>
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="arrow right"
          onClick={next}
          aria-label="Siguiente"
        >
          ›
        </button>
      </div>
    );
  };

  const renderHorizontalTimeline = () => (
    <div className="timeline-wrapper">
      <div className="timeline-line" />

      <div className="timeline">
        {filteredEvents.map((e, i) => {
          const yearLine = anioOCorto(e);
          const borderColor = EVENT_COLORS[e.tipo] || "#999";

          return (
            <div
              key={e.id}
              ref={selectedIndex === i ? selectedRef : null}
              className={`timeline-event ${
                selectedIndex === i ? "active" : ""
              }`}
              onClick={() => setSelectedIndex(i)}
            >
              <div
                className="timeline-dot"
                style={{
                  backgroundColor: borderColor,
                }}
              />

              <div
                className="timeline-card"
                style={{ borderTop: `4px solid ${borderColor}` }}
              >
                <div className="timeline-year">{yearLine}</div>
                <div className="timeline-title">{e.title}</div>
                <div
                  className="timeline-card-type"
                  style={{
                    backgroundColor: borderColor,
                    color: timelineCardTypeTextColor(e.tipo),
                  }}
                >
                  {TIPO_LABEL[e.tipo] ?? e.tipo ?? "Evento"}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="timeline-container">
      <div
        className={`sgc-section-shell timeline-desktop-shell${
          filtersOpen ? "" : " is-filters-collapsed"
        }`}
      >
        {filtersOpen ? renderFiltersAside() : null}

        <div
          ref={desktopContentRef}
          className="sgc-section-content timeline-desktop-content sgc-filtros-main"
        >
          <div className="sgc-section-title timeline-section-title timeline-section-title--inline">
            <div className="sgc-section-title__main">
              <SectionTitleIcon sectionKey="timeline" />
              <div className="sgc-section-title__text">
                <div className="sgc-section-title__h">Línea de tiempo</div>
                <div className="sgc-section-title__p">
                  Explora los eventos históricos asociados al volcán.
                </div>
              </div>
            </div>
            {!filtersOpen ? (
              <button
                type="button"
                className="sgc-filtros-open"
                onClick={() => setFiltersOpen(true)}
                aria-label="Expandir filtros"
                data-sgc-tooltip="Mostrar filtros"
              >
                <i className="bi bi-funnel" aria-hidden /> <span>Filtros</span>
              </button>
            ) : null}
          </div>

          {!isMobile ? (
            <div className="timeline-desktop-main">
              <div className="timeline-hero-preview">
                {filteredEvents.length > 0 ? (
                  renderHeroPreview()
                ) : (
                  <div className="timeline-hero-empty" aria-hidden="true" />
                )}
              </div>
              {filteredEvents.length > 0 ? renderHorizontalTimeline() : null}
            </div>
          ) : null}

          {isMobile ? (
            filteredEvents.length === 0 ? (
              <p className="timeline-filter-empty">
                No hay datos para mostrar en Línea de tiempo
              </p>
            ) : (
              renderMobileEvents()
            )
          ) : null}
        </div>
      </div>

      {modalOpen && selectedEvent && (
        <div
          className="timeline-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="timeline-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="timeline-modal-close"
              onClick={() => setModalOpen(false)}
              aria-label="Cerrar"
            >
              ×
            </button>

            <div className="timeline-modal-header">
              <h3 className="seccion-titulo timeline-modal-title">
                {selectedEvent.title}
              </h3>
            </div>

            <div className="timeline-modal-body">
              {selectedEvent.imagen_evento && (
                <div className="timeline-modal-image">
                  <img
                    src={getMediaUrl(selectedEvent.imagen_evento)}
                    alt={selectedEvent.title}
                  />
                </div>
              )}

              <div
                className="descripcion-texto rich-text timeline-modal-description"
                dangerouslySetInnerHTML={{
                  __html: normalizeAdminRichText(
                    selectedEvent.descripcion || "",
                  ),
                }}
              />
            </div>
          </div>
        </div>
      )}

      {!isMobile ? (
        filteredEvents.length === 0 ? (
          <p className="timeline-filter-empty">
            No hay datos para mostrar en Línea de tiempo
          </p>
        ) : (
          <div className="timeline-body-full">
            <div className="timeline-main timeline-main--sgc timeline-main--full">
              <div
                className="timeline-tabla-wrap"
                aria-label="Tabla de eventos"
              >
                <div className="timeline-tabla-toolbar">
                  <div className="timeline-tabla-headtext">
                    <div className="timeline-tabla-title">Eventos</div>
                    <div className="timeline-tabla-subtitle">
                      Selecciona un evento para verlo arriba.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="timeline-excel-btn"
                    data-sgc-tooltip="Descargar Excel"
                    aria-label="Descargar Excel"
                    disabled={excelBusy || filteredEvents.length === 0}
                    onClick={async () => {
                      if (excelBusy || filteredEvents.length === 0) return;
                      try {
                        setExcelBusy(true);
                        await buildAndDownloadExcel({
                          filteredEvents,
                          volcanId,
                          volcanNombre,
                        });
                      } finally {
                        setExcelBusy(false);
                      }
                    }}
                  >
                    <img src={excelIcon} alt="" />
                    <span className="timeline-excel-btn__text">
                      Descargar Excel
                    </span>
                  </button>
                </div>
                <div className="timeline-tabla-scroll" ref={timelineTableRef}>
                  <table className="timeline-tabla">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Evento</th>
                        <th>Tipo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEvents.map((e, i) => {
                        const color = EVENT_COLORS[e.tipo] || "#999";
                        const tipo = TIPO_LABEL[e.tipo] || e.tipo || "—";
                        const raw = String(e?.fecha_inicial ?? "").trim();
                        const isJan01 = /^\d{4}-01-01\b/.test(raw);
                        const fecha = isJan01
                          ? anioOCorto(e)
                          : formatDateLongEsCO(e.fecha_inicial);
                        const filaActiva = selectedEvent?.id === e.id;
                        return (
                          <tr
                            key={e.id}
                            className={`timeline-tabla-fila${
                              filaActiva ? " timeline-tabla-fila--active" : ""
                            }`}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedIndex(i)}
                            onKeyDown={(ev) => {
                              if (ev.key === "Enter" || ev.key === " ") {
                                ev.preventDefault();
                                setSelectedIndex(i);
                              }
                            }}
                          >
                            <td
                              className="timeline-tabla-celda-fecha"
                              style={{ borderLeftColor: color }}
                            >
                              {fecha}
                            </td>
                            <td className="timeline-tabla-celda-evento">
                              {e.title}
                            </td>
                            <td className="timeline-tabla-celda-tipo">
                              <span
                                className="timeline-tabla-tipo-pill"
                                style={{
                                  backgroundColor: color,
                                  color: eventLabelTextColor(e.tipo),
                                }}
                              >
                                {tipo}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )
      ) : null}
    </div>
  );
}
